import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import InsightCard from './InsightCard';
import CollegeRecommendationsSection from './CollegeRecommendationsSection';
import ActionPlanSection from './ActionPlanSection';

const StrategicResultsRenderer = ({ 
  insights, 
  studentProfile,
  currentUser, 
  studentGradeLevel, 
  integrationStatus, 
  addCollegeToList, 
  addActionToTimeline, 
  isIntegrating,
  calculateTimelineMessage,
  showLoadingState = false
}) => {
  const studentName = currentUser?.displayName || currentUser?.name || currentUser?.first_name;
  const [loadingMessage, setLoadingMessage] = React.useState(0);
  const [expandedSections, setExpandedSections] = React.useState({
    executive: true,     // Executive summary always open
    strengths: true,     // Core insights open by default
    tactical: true,      // Tactical advantages open
    schools: false,      // School recommendations collapsed
    action: false        // Action plan collapsed
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Progressive loading messages
  const loadingMessages = [
    "Analyzing your competitive advantages...",
    "Mapping your strategic positioning...",
    "Identifying tactical deployment opportunities...",
    "Building your school targeting strategy...",
    "Creating your urgency-based action plan...",
    "Finalizing your comprehensive strategic playbook..."
  ];
  
  // Cycle through loading messages
  React.useEffect(() => {
    if (showLoadingState || (!insights || !insights.strategicAdvantage)) {
      const interval = setInterval(() => {
        setLoadingMessage(prev => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [showLoadingState, insights]);
  
  // Check if insights has nested structure from database
  // Handle both: { insights: {...} } and flat { strategicAdvantage: {...} }
  const actualInsights = insights?.insights || insights;
  
  // Check if student should see Strategic Intelligence section
  const isEngineeringStudent = currentUser?.id === 'd9ed0488-0678-4fe5-b77a-a1387ca8ed1f';
  
  // Show processing state if insights are not ready
  if (showLoadingState || !actualInsights || !actualInsights.strategicAdvantage) {
    console.log('🔍 StrategicResultsRenderer: Loading state check', {
      showLoadingState,
      hasInsights: !!insights,
      hasActualInsights: !!actualInsights,
      hasStrategicAdvantage: !!actualInsights?.strategicAdvantage,
      insightsKeys: insights ? Object.keys(insights) : []
    });
    
    return (
      <div className="text-center py-16">
        <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-12 max-w-2xl mx-auto shadow-xl">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Crafting Your Strategic Profile</h2>
          <p className="text-stone-600 leading-relaxed mb-4">
            We're conducting a comprehensive analysis of your unique profile to create your personalized strategic playbook.
          </p>
          
          {/* Progressive loading indicator */}
          <div className="bg-gradient-to-r from-teal-50 to-pink-50 rounded-xl p-4 mb-4">
            <p className="text-sm font-medium text-slate-700 animate-pulse">
              {loadingMessages[loadingMessage]}
            </p>
          </div>
          
          <div className="mt-6 bg-teal-50 border border-teal-200 rounded-xl p-4">
            <p className="text-sm text-teal-700">
              <strong>Building your complete strategic report...</strong> This includes tactical advantages, competitive positioning, school recommendations, and urgency-based action planning.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      {/* Header Section */}
      <div className="text-center mb-16 bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-12 shadow-xl relative overflow-hidden">
        {/* Top gradient bar */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-400 via-pink-500 to-yellow-500"></div>
        
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-7 py-3 rounded-full text-sm font-bold inline-block mb-6 uppercase tracking-wider shadow-lg">
          Assessment Complete
        </div>
        
        <h1 className="text-5xl font-extrabold mb-4 text-slate-900 tracking-tight">
          {studentName ? `${studentName}'s Strategic Profile` : 'Your Strategic Profile'}
        </h1>
        
        <p className="text-xl text-stone-500 mb-8 font-medium">
          Here's what makes you unique and how to leverage it for college admissions.
        </p>
        
        {/* Strategic Advantage TL;DR */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200 rounded-2xl p-6 max-w-3xl mx-auto">
          <div className="text-sm font-bold text-yellow-700 uppercase tracking-wide mb-3">
            Your Strategic Advantage
          </div>
          <div className="text-lg font-semibold text-slate-900 leading-relaxed">
            {typeof actualInsights.strategicAdvantage === 'string' 
              ? actualInsights.strategicAdvantage 
              : actualInsights.strategicAdvantage?.primaryAdvantage || actualInsights.strategicAdvantage?.competitiveHeadline}
          </div>
        </div>
      </div>

      {/* Executive Summary Section - New Hero Card */}
      {(actualInsights.magneticHook || actualInsights.surpriseInsight) && (
        <div className="mb-12 bg-gradient-to-br from-teal-50 via-white to-pink-50 border-2 border-stone-200 rounded-3xl p-8 shadow-xl">
          <div className="flex items-start gap-4 mb-6">
            <div className="text-4xl">🎯</div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Your Magnetic Hook</h2>
              <p className="text-lg text-slate-700 leading-relaxed font-medium">
                {actualInsights.magneticHook}
              </p>
            </div>
          </div>
          {actualInsights.surpriseInsight && (
            <div className="bg-white rounded-2xl p-6 border border-stone-200">
              <div className="flex items-start gap-3">
                <div className="text-2xl">💡</div>
                <div>
                  <div className="text-sm font-bold text-teal-600 uppercase tracking-wide mb-1">Surprise Discovery</div>
                  <p className="text-slate-700">{actualInsights.surpriseInsight}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Strategic Intelligence Section - Only for Engineering Students */}
      {isEngineeringStudent && (
        <div className="mb-12 bg-gradient-to-br from-slate-50 via-white to-azure-50 border-2 border-slate-200 rounded-3xl p-8 shadow-xl">
          <div className="flex items-start gap-4 mb-6">
            <div className="text-4xl">🎖️</div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Strategic Intelligence</h2>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
                <p className="text-lg text-slate-700 leading-relaxed font-medium">
                You know how to deconstruct problems and find better solutions that others can't see, and your constant calculations and strategic risk analyses make you a master at maximizing efficiency.  Here's how to make sure that works for you - not against you.
                </p>
              </div>
              
              <div className="grid gap-6 md:grid-cols-2">
                {/* Problem Card */}
                <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="text-2xl">⚠️</div>
                    <div className="text-sm font-bold text-red-700 uppercase tracking-wide">
                      The Trap
                    </div>
                  </div>
                  <p className="text-slate-700 leading-relaxed">
                  Most applicants apply the same efficiency strategies that work in academic systems - understand expectations, meet every requirement, demonstrate capability. But when thousands of qualified students do this, they all sound identical. The system design creates this trap unintentionally.
                  </p>
                </div>
                
                {/* Opportunity Card */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-50 border-2 border-emerald-200 rounded-xl p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="text-2xl">🚀</div>
                    <div className="text-sm font-bold text-emerald-700 uppercase tracking-wide">
                      Your Advantage
                    </div>
                  </div>
                  <p className="text-slate-700 leading-relaxed">
                  You can evaluate the admissions process analytically and position yourself strategically. While others submit generic "super student" applications that check every top box, you could demonstrate actual systems thinking that engineering programs desperately need. But only if you give yourself time to showcase what makes your brain different - and your potential unmatched.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Core Strategic Insights Section */}
      <div className="mb-12">
        <div 
          className="flex items-center justify-between mb-6 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => toggleSection('strengths')}
        >
          <h2 className="text-2xl font-bold text-slate-900">Core Strategic Insights</h2>
          <div className="flex items-center gap-2 text-slate-600">
            <span className="text-sm font-medium">{expandedSections.strengths ? 'Collapse' : 'Expand'}</span>
            {expandedSections.strengths ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        
        {expandedSections.strengths && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fadeIn">
        {/* Intellectual Signature Card */}
        {actualInsights.intellectualSignature && (
          <InsightCard 
            type="signature"
            icon="🧮"
            title={actualInsights.intellectualSignature.title}
            subtitle={actualInsights.intellectualSignature.subtitle}
            content={actualInsights.intellectualSignature.content}
            leveragePoints={actualInsights.intellectualSignature.leveragePoints}
            strategicAngle={actualInsights.intellectualSignature.strategicAngle}
          />
        )}

        {/* Hidden Superpowers Card */}
        {actualInsights.hiddenSuperpowers && (
          <InsightCard 
            type="superpowers"
            icon="⚡"
            title={actualInsights.hiddenSuperpowers.title}
            subtitle={actualInsights.hiddenSuperpowers.subtitle}
            content={actualInsights.hiddenSuperpowers.content}
            superpowers={actualInsights.hiddenSuperpowers.superpowers}
            narrative={actualInsights.hiddenSuperpowers.narrative}
            competitiveEdge={actualInsights.hiddenSuperpowers.competitiveEdge}
          />
        )}

        {/* Tactical Advantages Card - NEW */}
        {actualInsights.tacticalAdvantages && (
          <InsightCard 
            type="tactical"
            icon="⚔️"
            title={actualInsights.tacticalAdvantages.title}
            subtitle={actualInsights.tacticalAdvantages.subtitle}
            content={actualInsights.tacticalAdvantages}
          />
        )}

        {/* Strategic Positioning Card */}
        {actualInsights.strategicPositioning && (
          <InsightCard 
            type="positioning"
            icon="🎯"
            title={actualInsights.strategicPositioning.title || "How to Stand Out"}
            subtitle={actualInsights.strategicPositioning.subtitle || "Your strategic positioning and competitive advantage"}
            content={actualInsights.strategicPositioning}
          />
        )}

        {/* Essay Strategy Card */}
        {actualInsights.essayStrategy && (
          <InsightCard 
            type="essays"
            icon="✍️"
            title={actualInsights.essayStrategy.title || "Essay Game Plan"}
            subtitle={actualInsights.essayStrategy.subtitle || "Your strategic approach to essays"}
            content={actualInsights.essayStrategy}
          />
        )}

        {/* Success Metrics Card - NEW */}
        {actualInsights.successMetrics && (
          <InsightCard 
            type="metrics"
            icon="📊"
            title={actualInsights.successMetrics.title || "Your Success Metrics"}
            subtitle={actualInsights.successMetrics.subtitle || "Measurable milestones to track your progress"}
            content={actualInsights.successMetrics}
          />
        )}
          </div>
        )}
      </div>

      {/* College Recommendations Section - Collapsible */}
      {actualInsights.collegeRecommendations && (
        <div className="mb-12">
          <div 
            className="flex items-center justify-between mb-6 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => toggleSection('schools')}
          >
            <h2 className="text-2xl font-bold text-slate-900">School Targeting Strategy</h2>
            <div className="flex items-center gap-2 text-slate-600">
              <span className="text-sm font-medium">{expandedSections.schools ? 'Collapse' : 'Expand'}</span>
              {expandedSections.schools ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>
          
          {expandedSections.schools && (
            <div className="animate-fadeIn">
              <CollegeRecommendationsSection 
                recommendations={actualInsights.collegeRecommendations}
                integrationStatus={integrationStatus}
                addCollegeToList={addCollegeToList}
                isIntegrating={isIntegrating}
              />
            </div>
          )}
        </div>
      )}

      {/* Action Plan Section - Collapsible */}
      {actualInsights.actionPlan && (
        <div className="mb-12">
          <div 
            className="flex items-center justify-between mb-6 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => toggleSection('action')}
          >
            <h2 className="text-2xl font-bold text-slate-900">Urgency-Based Action Plan</h2>
            <div className="flex items-center gap-2 text-slate-600">
              <span className="text-sm font-medium">{expandedSections.action ? 'Collapse' : 'Expand'}</span>
              {expandedSections.action ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>
          
          {expandedSections.action && (
            <div className="animate-fadeIn">
              <ActionPlanSection 
                actionPlan={actualInsights.actionPlan} 
                studentGradeLevel={studentGradeLevel}
                integrationStatus={integrationStatus}
                addActionToTimeline={addActionToTimeline}
                isIntegrating={isIntegrating}
                calculateTimelineMessage={calculateTimelineMessage}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default StrategicResultsRenderer;